import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { isAuth } from "../../helpers/cookies";

import {
  api_get_company_users_url,
  api_get_company_list_url,
  api_create_account_url,
  api_activate_deactivate_users_url,
  api_get_user_details_url,
  api_update_account_url,
  api_get_company_process_url
} from "./accountApi"
import {
  parser_get_company_users,
  parser_get_company_list,
  parser_create_acount,
  parser_activate_deactivate_users,
  parser_get_user_details,
  parser_update_acount,
  parser_get_company_process
} from "./accountParser"
import { PAGE } from "../../helpers/constants";

export const get_company_users = createAsyncThunk(
  "get_company_users",
  async (payload) => {
    try {
      const { company_id, qc, qn, page, limit } = payload
      // Use company_id from payload if available (e.g. 13), fallback to current user's company
      const target_company_id = company_id || isAuth()?.company_id;
      const response = await api_get_company_users_url(target_company_id, qc, qn, page, limit);
      const data = parser_get_company_users(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);


export const get_company_list = createAsyncThunk(
  "get_company_list",
  async (payload) => {
    try {
      const response = await api_get_company_list_url(payload);
      const data = parser_get_company_list(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const get_company_process = createAsyncThunk(
  "get_company_process",
  async (payload) => {
    try {
      const response = await api_get_company_process_url(payload);
      const data = parser_get_company_process(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const create_account = createAsyncThunk(
  "create account",
  async (payload) => {
    try {
      const response = await api_create_account_url(payload);
      const data = parser_create_acount(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const activate_deactivate_users = createAsyncThunk(
  "activate_deactivate_users",
  async (payload) => {
    try {
      const { user_id, updated_data } = payload
      const response = await api_activate_deactivate_users_url(user_id, updated_data)
      const data = parser_activate_deactivate_users(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const get_user_details = createAsyncThunk(
  "get_user_details",
  async (payload) => {
    try {
      const response = await api_get_user_details_url(payload);
      const data = parser_get_user_details(response);
      // console.log(data)
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const update_account = createAsyncThunk(
  "update account",
  async (payload) => {
    try {
      const { user_id, updated_data } = payload
      const response = await api_update_account_url(user_id, updated_data);
      const data = parser_update_acount(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);


const initialState = {
  is_account_modal_open: false,
  common_loading: false,
  selected_company: '',
  search_query: '',
  total_users_count: 0,
  active_users: 0,
  company_users: [],
  company_list: [],
  accounts: [],
  selected_data: null,
  c_action: null,
  user_id: null,
  page: PAGE - 1,
  defaultPage: null,
  company_process: []
};
export const accountSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    setAccountModalOpen: (state, action) => {
      state.is_account_modal_open = action.payload;
    },
    setAction: (state, action) => {
      state.c_action = action.payload;
    },
    setId: (state, action) => {
      state.user_id = action.payload;
    },
    setCompany: (state, action) => {
      state.selected_company = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.search_query = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload === null ? 0 : action.payload;
      state.defaultPage = action.payload !== null ? action.payload : null;
    },
  },
  extraReducers: (builder) => {
    // get company users list
    builder.addCase(get_company_users.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(get_company_users.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_users = action.payload.data.users;
      state.total_users_count = action.payload.data.total_count;
      state.active_users = action.payload.data.users.filter(user => user.is_active === true).length;
    })
    builder.addCase(get_company_users.rejected, (state) => {
      state.common_loading = false;
    })

    // get company list
    builder.addCase(get_company_list.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(get_company_list.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_list = action.payload.data.company;
    })
    builder.addCase(get_company_list.rejected, (state) => {
      state.common_loading = false;
    })

    //create account
    builder.addCase(create_account.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(create_account.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_users.push(action.payload.data);
    })
    builder.addCase(create_account.rejected, (state) => {
      state.common_loading = false;
    })
    // activate deactivate users
    builder.addCase(activate_deactivate_users.pending, (state) => {
      state.common_loading = true;
    });
    builder.addCase(activate_deactivate_users.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_users = state.company_users.map((user) =>
        user.id === action.payload.data.id ? { ...user, ...action.payload.data } : user
      );
    });
    builder.addCase(activate_deactivate_users.rejected, (state) => {
      state.common_loading = false;
    })
    //get user details
    builder.addCase(get_user_details.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(get_user_details.fulfilled, (state, action) => {
      state.common_loading = false;
      state.selected_data = action.payload.data
    })
    builder.addCase(get_user_details.rejected, (state) => {
      state.common_loading = false;
    })
    //update account
    builder.addCase(update_account.pending, (state) => {
      state.common_loading = true;
    });
    builder.addCase(update_account.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_users = state.company_users.map((user) =>
        user.id === action.payload.data.id ? { ...user, ...action.payload.data } : user
      );
    })
    builder.addCase(update_account.rejected, (state) => {
      state.common_loading = false;
    });
    // Get Company Process
    builder.addCase(get_company_process.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(get_company_process.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_process = action.payload.data
    })
    builder.addCase(get_company_process.rejected, (state) => {
      state.common_loading = false;
    })
  }
});

// Action creators are generated for each case reducer function
export const {
  setAccountModalOpen,
  setAction,
  setId,
  setCompany,
  setSearchQuery,
  setPage
} = accountSlice.actions;

export default accountSlice.reducer;